import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * StrikeActivityRecent — Strike Activity Signal (Last 2 Recent snapshots)
 *
 * Same directional logic as StrikeRangeShift, but ONLY compares the current (live)
 * snapshot with the immediately previous snapshot (last 2 recent).
 *
 * BULLISH when:
 *   - Strikes Dropped PE Side (last 2 recent) == Lower strikes
 *   - Strikes Entered CE Side (last 2 recent) == Higher strikes
 *   - Strikes Dropped CE Side (last 2 recent) == Lower strikes
 *   - Strikes Entered PE Side (last 2 recent) == Higher strikes
 *
 * BEARISH when:
 *   - Strikes Dropped PE Side (last 2 recent) == Higher strikes
 *   - Strikes Entered CE Side (last 2 recent) == Lower strikes
 *   - Strikes Dropped CE Side (last 2 recent) == Higher strikes
 *   - Strikes Entered PE Side (last 2 recent) == Lower strikes
 */
export const evaluateStrikeActivityRecent: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, activeRange, gap, settingsMode, baseSnapshot } = context;
    if (!baseSnapshot) return null;

    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;
    
    const trendAnalysisResult = context.liveIndicators?.trendAnalysisData;
    const fullTrendData = Array.isArray(trendAnalysisResult) ? trendAnalysisResult : (trendAnalysisResult?.data || []);
    if (fullTrendData.length < 2) return null;

    const topN = 10;

    const cellDataKeys: Record<string, string> = {
        'Combined OI': 'combOI',
        'Combined Volume': 'combVol',
        'Top OI': 'oi',
        'Top Volume': 'volume',
        'Top Chng OI': 'oiChg',
    };
    const valKey = cellDataKeys[settingsMode] || 'oi';

    // ─── Apply Fixed Anchor to Prevent Sliding Window Whipsaws ───
    const liveSpotPrice = fullTrendData[0]?.spotPrice || fullTrendData[0]?.indexLtp || spot;
    const fixedAtmStrike = Math.round(liveSpotPrice / gap) * gap;
    const fixedMinStrike = fixedAtmStrike - (activeRange * gap);
    const fixedMaxStrike = fixedAtmStrike + (activeRange * gap);

    // ─── Helper: Calculate ITM strikes for a single row using Fixed Anchor ───
    const calcITM = (row: any) => {
        const rawOpts = row.flatOpts || row.rawOpts || [];
        
        if (rawOpts.length === 0) return [];
        
        // 1. Filter by the identical locked window for ALL rows
        const inRangeRow = rawOpts.filter((opt: any) => opt.strike >= fixedMinStrike && opt.strike <= fixedMaxStrike);
        
        // 2. Determine ITM status using the fixed boundary, so spot price wiggles don't cause fake "Drops"
        const itmOpts = inRangeRow.filter((opt: any) => {
            if (opt.strike === fixedAtmStrike) return true;
            const isCE = opt.type === 'CE';
            return isCE ? opt.strike < fixedAtmStrike : opt.strike > fixedAtmStrike;
        });
        
        const uniqueItmOpts: any[] = [];
        const seen = new Set<string>();
        for (const opt of itmOpts) {
            const key = `${opt.strike}_${opt.type}`;
            if (!seen.has(key)) {
                seen.add(key);
                uniqueItmOpts.push(opt);
            }
        }
        
        uniqueItmOpts.sort((a, b) => (Number(b[valKey]) || 0) - (Number(a[valKey]) || 0));
        return uniqueItmOpts.slice(0, topN);
    };

    // ─── Current vs Previous ───
    let status = 'Sideways';
    let latestDroppedCE: number[] = [];
    let latestDroppedPE: number[] = [];
    let latestEnteredCE: number[] = [];
    let latestEnteredPE: number[] = [];
    let latestBullScore = 0;
    let latestBearScore = 0;

    // Iterate backwards through history to find the most recent persistent signal
    for (let i = 0; i < fullTrendData.length - 1; i++) {
        const liveRow = fullTrendData[i];
        const prevRow = fullTrendData[i + 1];
        
        const liveITM = calcITM(liveRow);
        const prevITM = calcITM(prevRow);
        
        if (liveITM.length === 0 || prevITM.length === 0) continue;

        const liveCE = new Set<number>(liveITM.filter((o: any) => o.type === 'CE').map((o: any) => o.strike as number));
        const livePE = new Set<number>(liveITM.filter((o: any) => o.type === 'PE').map((o: any) => o.strike as number));
        
        const prevCE = new Set<number>(prevITM.filter((o: any) => o.type === 'CE').map((o: any) => o.strike as number));
        const prevPE = new Set<number>(prevITM.filter((o: any) => o.type === 'PE').map((o: any) => o.strike as number));

        const liveRawOptsArr = liveRow.flatOpts || liveRow.rawOpts || context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
        const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);

        const inRange = liveFlatOpts.filter((opt: any) => opt.strike >= fixedMinStrike && opt.strike <= fixedMaxStrike);
        const validCE = new Set(inRange.filter((o: any) => o.type === 'CE').map((o: any) => o.strike));
        const validPE = new Set(inRange.filter((o: any) => o.type === 'PE').map((o: any) => o.strike));

        const droppedCE = Array.from(prevCE).filter(s => !liveCE.has(s) && validCE.has(s)).sort((a, b) => a - b);
        const droppedPE = Array.from(prevPE).filter(s => !livePE.has(s) && validPE.has(s)).sort((a, b) => a - b);
        const enteredCE = Array.from(liveCE).filter(s => !prevCE.has(s)).sort((a, b) => a - b);
        const enteredPE = Array.from(livePE).filter(s => !prevPE.has(s)).sort((a, b) => a - b);

        if (droppedCE.length === 0 && droppedPE.length === 0 && enteredCE.length === 0 && enteredPE.length === 0) continue;

        const getAverage = (arr: number[]) => {
            if (arr.length === 0) return 0;
            return arr.reduce((sum, val) => sum + val, 0) / arr.length;
        };

        const bullPEShift = droppedPE.length > 0 && enteredPE.length > 0 && getAverage(droppedPE) < getAverage(enteredPE);
        const bullCEShift = droppedCE.length > 0 && enteredCE.length > 0 && getAverage(droppedCE) < getAverage(enteredCE);
        const bearPEShift = droppedPE.length > 0 && enteredPE.length > 0 && getAverage(droppedPE) > getAverage(enteredPE);
        const bearCEShift = droppedCE.length > 0 && enteredCE.length > 0 && getAverage(droppedCE) > getAverage(enteredCE);

        const bullScore = [bullPEShift, bullCEShift].filter(Boolean).length;
        const bearScore = [bearPEShift, bearCEShift].filter(Boolean).length;

        if (bullScore >= 1 && bullScore > bearScore) status = 'BULL';
        else if (bearScore >= 1 && bearScore > bullScore) status = 'BEAR';

        if (status !== 'Sideways') {
            latestDroppedCE = droppedCE;
            latestDroppedPE = droppedPE;
            latestEnteredCE = enteredCE;
            latestEnteredPE = enteredPE;
            latestBullScore = bullScore;
            latestBearScore = bearScore;
            break; // Found the most recent persistent signal, stop looking back
        }
    }

    if (status === 'Sideways') return null;

    // ─── Entry Strike (ATM option) ───
    let entryPrice = 0;
    const optionType = status === 'BULL' ? 'CE' : 'PE';
    
    const liveRawOptsArr = fullTrendData[0]?.flatOpts || fullTrendData[0]?.rawOpts || context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);

    const atmRow = liveFlatOpts.find(r => r.strike === fixedAtmStrike && r.type === optionType);
    if (atmRow) {
        entryPrice = atmRow.ltp || atmRow.close || atmRow.price || 0;
    }
    
    if (entryPrice <= 0) return null;

    return {
        trade_type: status as any,
        strike: fixedAtmStrike,
        option_type: optionType,
        entry_price: entryPrice,
        remarks: `${status} Recent (Drop CE:${latestDroppedCE.join(',')||'-'} PE:${latestDroppedPE.join(',')||'-'} | Enter CE:${latestEnteredCE.join(',')||'-'} PE:${latestEnteredPE.join(',')||'-'} | Bull:${latestBullScore}/4 Bear:${latestBearScore}/4)`
    };
};
